// Language Detection Service for Automatic Content Filtering
import { COUNTRY_LANGUAGE_MAPPING as HARDCODE_FALLBACK, LanguagePreference, CountryLanguageInfo } from './countryLanguageMapping';

// Re-export types for backward compatibility

export interface UserLanguageContext {
  countryCode: string;
  browserLanguages: string[];
  selectedLanguage: LanguagePreference;
  fallbackToFirstLanguage: boolean;
}

export interface CloudflareTraceData {
  loc: string; // Country code
  ip: string;
  [key: string]: string;
}

class LanguageDetectionService {
  private static instance: LanguageDetectionService;
  private userLanguageContext: UserLanguageContext | null = null;
  private isInitialized = false;
  
  // 🌍 DYNAMIC LANGUAGE DATABASE
  private dynamicLanguageMapping: Record<string, CountryLanguageInfo> | null = null;
  private isLanguageMappingLoaded = false;
  private languageMappingPromise: Promise<Record<string, CountryLanguageInfo>> | null = null;

  private constructor() {}

  public static getInstance(): LanguageDetectionService {
    if (!LanguageDetectionService.instance) {
      LanguageDetectionService.instance = new LanguageDetectionService();
    }
    return LanguageDetectionService.instance;
  }

  /**
   * 🌍 Load language mappings from public/lang.json with hardcode fallback
   */
  private async loadLanguageMappings(): Promise<Record<string, CountryLanguageInfo>> {
    // Return cached result if already loaded
    if (this.isLanguageMappingLoaded && this.dynamicLanguageMapping) {
      console.log('🌍 Using cached language mappings (200+ countries)');
      return this.dynamicLanguageMapping;
    }

    // Return existing promise if already loading
    if (this.languageMappingPromise) {
      return this.languageMappingPromise;
    }

    // Create new loading promise
    this.languageMappingPromise = this.fetchLanguageMappings();
    
    try {
      const result = await this.languageMappingPromise;
      this.languageMappingPromise = null; // Clear promise after completion
      return result;
    } catch (error) {
      this.languageMappingPromise = null; // Clear promise on error
      throw error;
    }
  }

  /**
   * 🌍 Fetch language mappings from HTTP with fallback safety
   */
  private async fetchLanguageMappings(): Promise<Record<string, CountryLanguageInfo>> {
    try {
      console.log('🌍 Loading dynamic language mappings from /lang.json...');
      
      const response = await fetch('/lang.json', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'public, max-age=3600' // Cache for 1 hour
        },
        cache: 'default'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const langData = await response.json();
      
      // Validate data structure
      if (!langData || typeof langData !== 'object') {
        throw new Error('Invalid language data structure');
      }

      // Count countries and languages
      const countryCount = Object.keys(langData).length;
      const languageCount = Object.values(langData as Record<string, CountryLanguageInfo>)
        .reduce((total, country) => total + country.languages.length, 0);

      console.log(`🌍 ✅ DYNAMIC MAPPINGS LOADED: ${countryCount} countries, ${languageCount} languages`);
      
      // Cache the loaded data
      this.dynamicLanguageMapping = langData;
      this.isLanguageMappingLoaded = true;
      
      return langData;
    } catch (error) {
      console.error('❌ Failed to load dynamic language mappings:', error);
      console.log('🔧 FALLING BACK to hardcode mappings (4 countries)');
      
      // Use hardcode fallback for safety
      this.dynamicLanguageMapping = HARDCODE_FALLBACK;
      this.isLanguageMappingLoaded = true;
      
      return HARDCODE_FALLBACK;
    }
  }

  /**
   * 🌍 Get effective language mapping (dynamic or fallback)
   */
  private async getEffectiveLanguageMapping(): Promise<Record<string, CountryLanguageInfo>> {
    return await this.loadLanguageMappings();
  }

  /**
   * Initialize language detection by calling Cloudflare trace API
   * and matching with browser preferences - UNIVERSAL MULTILINGUAL SYSTEM
   */
  public async initializeLanguageDetection(): Promise<UserLanguageContext> {
    try {
      if (this.userLanguageContext && this.isInitialized) {
        return this.userLanguageContext;
      }

      console.log('🌍 Starting universal multilingual language detection...');
      
      // Get browser languages for priority matching
      const originalBrowserLanguages = this.getOriginalBrowserLanguages();
      const browserLanguages = this.getBrowserLanguagePreferences();
      

      
      // Step 1: Get country from Cloudflare trace API
      const countryCode = await this.getCountryFromCloudflare();
      console.log(`🌍 User country detected: ${countryCode}`);

      // Step 2: Find matching language using our comprehensive database
      const selectedLanguage = await this.findBestMatchingLanguage(countryCode, browserLanguages);
      console.log(`🌍 Selected language: ${selectedLanguage.language_code} (ID: ${selectedLanguage.language_id})`);

      // Step 3: Determine if we fell back to first language (check if any browser lang matched)
      const countryLanguages = await this.getCountryLanguages(countryCode);
      const hasDirectMatch = browserLanguages.some(lang => {
        const normalizedBrowserLang = this.normalizeLanguageCode(lang);
        return countryLanguages.some(countryLang => 
          countryLang.language_code === normalizedBrowserLang
        );
      });

      // Step 4: Create user language context
      this.userLanguageContext = {
        countryCode,
        browserLanguages,
        selectedLanguage,
        fallbackToFirstLanguage: !hasDirectMatch
      };

      this.isInitialized = true;
      console.log('🌍 Universal multilingual detection completed:', this.userLanguageContext);
      
      return this.userLanguageContext;
    } catch (error) {
      console.error('❌ Language detection failed:', error);
      
      // 🔧 UNIVERSAL FALLBACK: Smart fallback based on browser language
      const browserLanguages = this.getBrowserLanguagePreferences();
      const fallbackResult = await this.getUniversalFallbackLanguage(browserLanguages);
      
      this.userLanguageContext = {
        countryCode: fallbackResult.country,
        browserLanguages,
        selectedLanguage: fallbackResult.language,
        fallbackToFirstLanguage: true
      };

      this.isInitialized = true;
      console.log(`🌍 Universal fallback completed (${fallbackResult.language.language_code}):`, this.userLanguageContext);
      
      return this.userLanguageContext;
    }
  }

  /**
   * Get original browser language preferences (before normalization)
   */
  private getOriginalBrowserLanguages(): string[] {
    try {
      const languages: string[] = [];
      
      // Get languages from navigator.languages (modern browsers)
      if (navigator.languages && navigator.languages.length > 0) {
        languages.push(...navigator.languages);
      } else if (navigator.language) {
        // Fallback to single language
        languages.push(navigator.language);
      }

      return languages;
    } catch (error) {
      console.error('❌ Failed to get original browser languages:', error);
      return ['en']; // Fallback to English
    }
  }

  /**
   * Get country code from Cloudflare trace API
   */
  private async getCountryFromCloudflare(): Promise<string> {
    try {
      const response = await fetch('https://www.infonium.org/cdn-cgi/trace', {
        method: 'GET',
        headers: {
          'Accept': 'text/plain',
        },
        cache: 'no-cache'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const traceText = await response.text();
      console.log('🌍 Cloudflare trace response:', traceText);

      // Parse the trace response
      const traceData: Record<string, string> = {};
      traceText.split('\n').forEach(line => {
        const [key, value] = line.split('=');
        if (key && value) {
          traceData[key.trim()] = value.trim();
        }
      });

      const countryCode = traceData.loc;
      if (!countryCode) {
        throw new Error('Country code not found in trace response');
      }

      return countryCode.toUpperCase();
    } catch (error) {
      console.error('❌ Failed to get country from Cloudflare:', error);
      // Fallback to US if detection fails
      return 'US';
    }
  }

  /**
   * Get browser language preferences from Accept-Language header simulation
   * MAINTAINS PRIORITY ORDER - first language has highest priority
   */
  private getBrowserLanguagePreferences(): string[] {
    try {
      const languages: string[] = [];
      
      // Get languages from navigator.languages (modern browsers)
      if (navigator.languages && navigator.languages.length > 0) {
        languages.push(...navigator.languages);
      } else if (navigator.language) {
        // Fallback to single language
        languages.push(navigator.language);
      }

      console.log('🌍 Original browser languages (with priority):', languages);
      
      // Normalize but preserve order - higher index = lower priority
      const normalizedLanguages = languages
        .map(lang => this.normalizeLanguageCode(lang))
        .filter((lang, index, arr) => arr.indexOf(lang) === index); // Remove duplicates but preserve order

      console.log('🌍 Normalized browser languages (priority order):', normalizedLanguages);
      return normalizedLanguages;
    } catch (error) {
      console.error('❌ Failed to get browser languages:', error);
      return ['en']; // Fallback to English
    }
  }

  /**
   * Normalize language code (en-US -> en, pt-BR -> pt)
   */
  private normalizeLanguageCode(languageCode: string): string {
    return languageCode.split('-')[0].toLowerCase();
  }



  /**
   * Find the best matching language based on country and browser preferences
   */
  private async findBestMatchingLanguage(
    countryCode: string, 
    browserLanguages: string[]
  ): Promise<LanguagePreference> {
    try {
      console.log(`🌍 Country-Browser matching: ${countryCode} + [${browserLanguages.join(', ')}]`);
      
      // Get allowed languages for this country from database
      const countryLanguages = await this.getCountryLanguages(countryCode);
      
      if (countryLanguages.length === 0) {
        console.warn(`⚠️ No languages found for country: ${countryCode}, using universal fallback`);
        const fallback = await this.getUniversalFallbackLanguage(browserLanguages);
        return fallback.language;
      }

      console.log(`🌍 Country ${countryCode} supports: [${countryLanguages.map(l => l.language_code).join(', ')}]`);

      // Match browser languages against country-allowed languages
      const browserMatches: Array<{lang: LanguagePreference, browserPriority: number}> = [];
      
      for (let i = 0; i < browserLanguages.length; i++) {
        const browserLang = browserLanguages[i];
        const normalizedBrowserLang = this.normalizeLanguageCode(browserLang);
        const matchingLanguage = countryLanguages.find(
          countryLang => countryLang.language_code === normalizedBrowserLang
        );
        
        if (matchingLanguage) {
          browserMatches.push({
            lang: matchingLanguage,
            browserPriority: i + 1 // Lower number = higher priority
          });
          console.log(`🎯 MATCH FOUND: "${browserLang}" (browser priority ${i + 1}) → ${matchingLanguage.language_code} (country priority ${matchingLanguage.priority})`);
        }
      }
      
      if (browserMatches.length > 0) {
        // Sort by browser priority first (user preference), then by country priority
        browserMatches.sort((a, b) => {
          if (a.browserPriority !== b.browserPriority) {
            return a.browserPriority - b.browserPriority; // Lower browser priority wins
          }
          return a.lang.priority - b.lang.priority; // Lower country priority wins  
        });
        
        const selectedMatch = browserMatches[0];
        console.log(`🏆 FINAL SELECTION: ${selectedMatch.lang.language_code} (browser priority ${selectedMatch.browserPriority}, country priority ${selectedMatch.lang.priority})`);
        return selectedMatch.lang;
      }

      // If no browser language matches country languages, use first country language
      console.log(`🔧 NO MATCH: Using default country language for ${countryCode}: ${countryLanguages[0].language_code} (ID: ${countryLanguages[0].language_id})`);
      return countryLanguages[0];
    } catch (error) {
      console.error('❌ Error finding matching language:', error);
      return { language_code: 'en', language_id: '11703', priority: 1 };
    }
  }

  /**
   * Get supported languages for a country
   */
  private async getCountryLanguages(countryCode: string): Promise<LanguagePreference[]> {
    const mapping = await this.getEffectiveLanguageMapping();
    const countryInfo = mapping[countryCode];
    return countryInfo?.languages || [];
  }

  /**
   * Get current user language context
   */
  public getCurrentLanguageContext(): UserLanguageContext | null {
    return this.userLanguageContext;
  }

  /**
   * Get current language category ID for API filtering
   */
  public getCurrentLanguageCategoryId(): string | null {
    return this.userLanguageContext?.selectedLanguage.language_id || null;
  }

  /**
   * Get current language code
   */
  public getCurrentLanguageCode(): string | null {
    return this.userLanguageContext?.selectedLanguage.language_code || null;
  }

  /**
   * Force refresh language detection
   */
  public async refreshLanguageDetection(): Promise<UserLanguageContext> {
    this.isInitialized = false;
    this.userLanguageContext = null;
    return this.initializeLanguageDetection();
  }

  /**
   * Manually set language (for testing or user override)
   */
  public setLanguage(languageCode: string, languageId: string): void {
    const selectedLanguage: LanguagePreference = {
      language_code: languageCode,
      language_id: languageId,
      priority: 1
    };

    this.userLanguageContext = {
      countryCode: this.userLanguageContext?.countryCode || 'US',
      browserLanguages: this.userLanguageContext?.browserLanguages || ['en'],
      selectedLanguage,
      fallbackToFirstLanguage: false
    };

    console.log('🌍 Language manually set:', this.userLanguageContext);
  }

  /**
   * Check if language detection is initialized
   */
  public isLanguageDetectionInitialized(): boolean {
    return this.isInitialized && this.userLanguageContext !== null;
  }

  /**
   * Get all available languages from our comprehensive database
   * Extracts unique languages from all country mappings
   */
  private async getAllAvailableLanguages(): Promise<LanguagePreference[]> {
    const allLanguages: LanguagePreference[] = [];
    const seenLanguages = new Set<string>();
    
    // Extract all languages from country mappings (dynamic HTTP + fallback)
    const mapping = await this.getEffectiveLanguageMapping();
    Object.values(mapping).forEach(countryInfo => {
      countryInfo.languages.forEach(lang => {
        const key = `${lang.language_code}:${lang.language_id}`;
        if (!seenLanguages.has(key)) {
          seenLanguages.add(key);
          allLanguages.push(lang);
        }
      });
    });
    
    console.log(`🌍 DATABASE: ${allLanguages.length} unique languages available in system`);
    return allLanguages;
  }

  /**
   * Predict most likely country based on language code
   * Used for fast-track language detection
   */
  private async predictCountryFromLanguage(languageCode: string): Promise<string> {
    const mapping = await this.getEffectiveLanguageMapping();
    
    // Find countries that have this language as primary/first language
    const countriesWithLanguage = Object.entries(mapping).filter(([, countryInfo]) => 
      countryInfo.languages.some(lang => lang.language_code === languageCode)
    );
    
    if (countriesWithLanguage.length === 0) {
      return 'US'; // Default fallback
    }
    
    // Prefer countries where this language is the first/primary language
    const primaryLanguageCountries = countriesWithLanguage.filter(([, countryInfo]) => 
      countryInfo.languages[0]?.language_code === languageCode
    );
    
    if (primaryLanguageCountries.length > 0) {
      const predictedCountry = primaryLanguageCountries[0][0];
      console.log(`🌍 Language prediction: "${languageCode}" → likely country: ${predictedCountry}`);
      return predictedCountry;
    }
    
    // Use first country that supports this language
    const fallbackCountry = countriesWithLanguage[0][0];
    console.log(`🌍 Language prediction: "${languageCode}" → fallback country: ${fallbackCountry}`);
    return fallbackCountry;
  }

  /**
   * 🌍 UNIVERSAL FALLBACK: Smart language selection based on browser preference
   * Returns the best possible language match for any browser language configuration
   */
  private async getUniversalFallbackLanguage(browserLanguages: string[]): Promise<{
    language: LanguagePreference;
    country: string;
  }> {
    console.log(`🌍 UNIVERSAL FALLBACK: Analyzing browser languages [${browserLanguages.join(', ')}]`);
    
    // Try to find exact match in our comprehensive database
    const directMatch = await this.findDirectBrowserLanguageMatch(browserLanguages);
    if (directMatch) {
      const predictedCountry = await this.predictCountryFromLanguage(directMatch.language_code);
      return {
        language: directMatch,
        country: predictedCountry
      };
    }
    
    // Language-to-Country prediction fallback
    for (const browserLang of browserLanguages) {
      const normalizedLang = this.normalizeLanguageCode(browserLang);
      const predictedCountry = await this.predictCountryFromLanguage(normalizedLang);
      const countryLanguages = await this.getCountryLanguages(predictedCountry);
      
      if (countryLanguages.length > 0) {
        console.log(`🌍 UNIVERSAL FALLBACK: "${browserLang}" → country: ${predictedCountry} → language: ${countryLanguages[0].language_code}`);
        return {
          language: countryLanguages[0],
          country: predictedCountry
        };
      }
    }
    
    // Final fallback: English (US)
    console.log('🌍 UNIVERSAL FALLBACK: Using final fallback (English/US)');
    return {
      language: { language_code: 'en', language_id: '11703', priority: 1 },
      country: 'US'
    };
  }

  /**
   * Find direct browser language match from our 200+ language database
   * Returns immediate match if browser language is directly supported
   */
  private async findDirectBrowserLanguageMatch(browserLanguages: string[]): Promise<LanguagePreference | null> {
    // Get all available languages from our database
    const allAvailableLanguages = await this.getAllAvailableLanguages();
    
    for (const browserLang of browserLanguages) {
      const normalizedBrowserLang = this.normalizeLanguageCode(browserLang);
      
      // Try exact match first
      const exactMatch = allAvailableLanguages.find(lang => 
        lang.language_code === normalizedBrowserLang
      );
      
      if (exactMatch) {
        console.log(`🎯 EXACT MATCH: Browser language "${browserLang}" -> ${exactMatch.language_code} (ID: ${exactMatch.language_id})`);
        return exactMatch;
      }
    }
    
    // Try partial matches (e.g., "en-US" matches "en")
    for (const browserLang of browserLanguages) {
      const baseLang = browserLang.split('-')[0].toLowerCase();
      
      const partialMatch = allAvailableLanguages.find(lang => 
        lang.language_code === baseLang
      );
      
      if (partialMatch) {
        console.log(`🎯 PARTIAL MATCH: Browser language "${browserLang}" -> ${partialMatch.language_code} (ID: ${partialMatch.language_id})`);
        return partialMatch;
      }
    }
    
    return null;
  }
}

// Export singleton instance
export const languageDetectionService = LanguageDetectionService.getInstance();
export default languageDetectionService;